import React, { useMemo, useState } from "react";
import { Box, IconButton, Stack, Typography, Button } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { keyframes } from "@mui/system";
import useThemeStore from "@/shared/stores/useThemeStore";
import ShakaPlayerVideo from "@/shared/components/shakaplayer/ShakaPlayerVideo";
import { useManagedCardFileVideo } from "@/cards/common/component/background-players/useManagedCardVideoPlayback";

type VideoExperienceData = {
  video: {
    videoUrl?: string;
    description?: string;
    title?: string;
    thumbnail?: string;
  };
};

type Props = {
  data: VideoExperienceData;
};

function isMovVideoUrl(url: string): boolean {
  return /\.mov(\?.*)?$/i.test(url);
}

const playPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.28);
  }
  50% {
    transform: scale(1.03);
    box-shadow: 0 0 0 14px rgba(255, 255, 255, 0);
  }
`;

export const TaasStaiExpVideo: React.FC<Props> = ({ data }) => {
  const { darkMode } = useThemeStore();

  const [isPlaying, setIsPlaying] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const videoUrl = data?.video?.videoUrl || "";
  const stripHtmlTags = (html: string) => {
    if (!html) return "";
    try {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    } catch {
      return html;
    }
  };

  const descriptionRaw = data?.video?.description || "";
  const description = stripHtmlTags(descriptionRaw);
  const title = data?.video?.title || "Video";
  const poster = data?.video?.thumbnail || undefined;

  const hasVideo = useMemo(() => Boolean(videoUrl && videoUrl.trim()), [videoUrl]);
  const useNativeVideo = isMovVideoUrl(videoUrl);

  // MOV → native <video> + shared mini-player handoff on leave
  const { videoRef } = useManagedCardFileVideo({
    mediaUrl: videoUrl,
    title,
    description,
    posterUrl: poster,
    enabled: hasVideo && useNativeVideo,
  });

  const shakaMiniPlayerMeta = useMemo(
    () => ({
      title,
      subtitle: description,
      mediaUrl: videoUrl,
      posterUrl: poster,
    }),
    [title, description, videoUrl, poster]
  );

  const handleTogglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused || el.ended) {
      el.play().catch(() => {
        // ignore play errors (e.g., autoplay restrictions)
      });
    } else {
      el.pause();
    }
  };

  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    setCurrentTime(el.currentTime);
    if (el.duration) {
      setDuration(el.duration);
    }
  };

  const handleLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.duration) {
      setDuration(el.duration);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = videoRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = Math.max(0, Math.min(duration, percentage * duration));
    el.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        background: darkMode ? "#111014" : "#f8f8f8",
        display: "flex",
        flexDirection: "column",
        color: darkMode ? "#f7f5fa" : "var(--neutralBlack)",
      }}
    >
      {title && (
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2.25, sm: 3 },
            pt: { xs: 2, sm: 2.5 },
            pb: { xs: 1.5, sm: 2 },
          }}
        >
          {title && (
            <Typography
              sx={{
                color: darkMode ? "#f7f5fa" : "var(--neutralBlack)",
                mb: description ? 0.6 : 0,
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.45rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              }}
            >
              {title}
            </Typography>
          )}

          {description && (
            <Box>
              <Typography
                sx={{
                  color: darkMode ? "rgba(247,245,250,0.68)" : "#655f70",
                  fontFamily: "Arial, sans-serif",
                  fontSize: { xs: "1rem", sm: "1.05rem" },
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  ...(showFullDescription
                    ? {}
                    : {
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }),
                }}
              >
                {description}
              </Typography>
              {description.length > 140 && (
                <Button
                  onClick={() => setShowFullDescription((prev) => !prev)}
                  sx={{
                    mt: 0.35,
                    p: 0,
                    minWidth: "auto",
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    color: darkMode ? "#ddd7e8" : "#4c4558",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {showFullDescription ? "See less" : "See more"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1.5, sm: 2.5 },
          pb: { xs: 1.5, sm: 2.5 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            maxWidth: 960,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            borderRadius: { xs: "14px", sm: "20px" },
            border: isPlaying
              ? "1px solid rgba(196,181,253,0.38)"
              : darkMode
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(26,21,40,0.1)",
            backgroundColor: "#0a0a0e",
            boxShadow: isPlaying
              ? "0 24px 56px rgba(109,40,217,0.22)"
              : darkMode
                ? "0 18px 40px rgba(0,0,0,0.3)"
                : "0 16px 40px rgba(26,21,40,0.14)",
            transition: "box-shadow 0.45s ease, border-color 0.45s ease",
          }}
        >
          {hasVideo ? (
            useNativeVideo ? (
              <>
                <video
                  ref={videoRef}
                  src={videoUrl}
                  poster={poster}
                  preload="metadata"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    background: "#0a0a0e",
                  }}
                />
                {!isPlaying && (
                  <Box
                    component="button"
                    type="button"
                    onClick={handleTogglePlay}
                    aria-label="Play video"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      border: 0,
                      background: "rgba(0,0,0,0.28)",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      component="span"
                      sx={{
                        width: { xs: 64, sm: 72 },
                        height: { xs: 64, sm: 72 },
                        color: "#fff",
                        background: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.32)",
                        animation: `${playPulse} 2.8s ease-in-out infinite`,
                        "&:hover": {
                          background: "linear-gradient(135deg, #9f7aea 0%, #7c3aed 100%)",
                        },
                      }}
                    >
                      <PlayArrowRoundedIcon sx={{ fontSize: 40, ml: 0.4 }} />
                    </IconButton>
                  </Box>
                )}
                {isPlaying && (
                  <IconButton
                    onClick={handleTogglePlay}
                    aria-label="Pause video"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      m: "auto",
                      width: 64,
                      height: 64,
                      color: "#fff",
                      backgroundColor: "rgba(15,23,42,0.72)",
                      opacity: 0,
                      zIndex: 2,
                      transition: "opacity 160ms ease",
                      "&:hover": {
                        opacity: 1,
                        backgroundColor: "rgba(15,23,42,0.88)",
                      },
                    }}
                  >
                    <PauseRoundedIcon sx={{ fontSize: 34 }} />
                  </IconButton>
                )}
              </>
            ) : (
              <ShakaPlayerVideo
                src={videoUrl}
                enableMiniPlayer
                miniPlayerMeta={shakaMiniPlayerMeta}
                onPlayStateChange={setIsPlaying}
              />
            )
          ) : (
            <Stack spacing={1} alignItems="center">
              <VideocamOffIcon
                sx={{
                  fontSize: 48,
                  color: darkMode ? "var(--neutral-300)" : "var(--neutral-100)",
                }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  color: darkMode ? "var(--neutral-300)" : "var(--neutral-100)",
                }}
              >
                Video unavailable
              </Typography>
            </Stack>
          )}

          {hasVideo && useNativeVideo && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: { xs: 12, sm: 16 },
                zIndex: 3,
                px: { xs: 2, sm: 2.5 },
                pointerEvents: "auto",
              }}
            >
              <Box
                onClick={handleSeek}
                sx={{
                  width: "100%",
                  height: 6,
                  borderRadius: 3,
                  background: "rgba(255,255,255,0.2)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.3)",
                  "&:hover": { height: 8 },
                  transition: "height 160ms ease",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: `${progress}%`,
                    background: "linear-gradient(90deg, #ffffff, rgba(255,255,255,0.78))",
                    borderRadius: 3,
                    transition: "width 0.1s linear",
                  }}
                />
              </Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.7, px: 0.25 }}>
                {[currentTime, duration].map((time, index) => (
                  <Typography
                    key={index}
                    sx={{
                      color: "rgba(249,250,251,0.72)",
                      fontSize: { xs: 10, sm: 11 },
                      fontWeight: 500,
                      fontVariantNumeric: "tabular-nums",
                      fontFamily: "monospace",
                      textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                    }}
                  >
                    {formatTime(time)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
